import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reportAPI, uploadAPI, woundAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, ArrowRight, Camera, Ruler, Stethoscope, Pill, Check, Upload, UserCheck, Target, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'foto', label: 'Foto', icon: Camera },
  { id: 'messung', label: 'Messung', icon: Ruler },
  { id: 'assessment', label: 'Assessment', icon: Stethoscope },
  { id: 'ursachen', label: 'Risiko', icon: Target },
  { id: 'behandlung', label: 'Therapie', icon: Pill },
  { id: 'arzt', label: 'Arzt', icon: UserCheck },
  { id: 'zusammenfassung', label: 'Fertig', icon: Check },
];

const WUNDRAND_OPTIONS = ['glatt', 'mazeriert', 'unterminiert', 'entzündet', 'Hyperkeratose', 'Taschenbildung'];
const WUNDUMGEBUNG_OPTIONS = ['intakt', 'gerötet', 'mazeriert', 'trocken', 'Schwellung', 'Ekzem', 'Induration', 'Ödem'];
const INFEKTIONSZEICHEN_OPTIONS = ['Rötung', 'Überwärmung', 'Schwellung', 'Schmerz', 'Funktionseinschränkung', 'Fieber'];
const URSACHEN_OPTIONS = ['Druck', 'Durchblutungsstörung', 'Diabetes', 'Immobilität', 'OP / Trauma'];
const PFLEGEZIELE_OPTIONS = ['Exsudatreduktion', 'Infektionsvermeidung', 'Förderung der Granulation', 'Schmerzlinderung', 'Wundheilung / Stabilisierung'];

export default function NewWoundReportPage() {
  const { woundId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wound, setWound] = useState(null);
  const [form, setForm] = useState({
    wound_id: woundId || '',
    foto: '',
    laenge: 0, breite: 0, tiefe: 0,
    wundgrund: { granulation: 0, fibrin: 0, nekrose: 0, epithelisierung: 0 },
    wundrand: [], wundumgebung: [],
    exsudat_menge: '', exsudat_art: '', exsudat_geruch: '',
    infektionszeichen: [],
    schmerz_nrs: 0, schmerz_qualitaet: '', schmerz_zeitpunkt: '',
    wundphase: '',
    // Ursachen & Risiko
    ursachen: [], braden_skala: 0, braden_datum: '', weitere_risiken: '',
    // Pflegeziele
    pflegeziele: [], zieltermin: '',
    // Behandlung
    behandlung_reinigung: '', behandlung_spuelloesung: '',
    behandlung_primaerverband: '', behandlung_sekundaerverband: '',
    behandlung_fixierung: '', behandlung_kompression: '',
    behandlung_lagerung: '', behandlung_medikamente: '',
    behandlung_begruendung: '', vw_frequenz: '', schmerzmanagement: '',
    // Arzt
    arzt_informiert: '', arzt_datum: '', arzt_anlass: '', verordnung_vorhanden: '',
    // Evaluation
    evaluation_status: '', evaluation_begruendung: '', massnahmen_angepasst: '',
    naechster_vw: '', bemerkungen: '',
    pflegefachkraft: user?.name || ''
  });

  useEffect(() => {
    if (woundId) loadWound();
  }, [woundId]);

  const loadWound = async () => {
    try {
      const res = await woundAPI.get(woundId);
      setWound(res.data);
      if (res.data.reports?.length > 0) {
        const latest = res.data.reports[0];
        setForm(prev => ({
          ...prev, wound_id: woundId,
          wundrand: latest.wundrand || [], wundumgebung: latest.wundumgebung || [],
          exsudat_menge: latest.exsudat_menge || '', exsudat_art: latest.exsudat_art || '',
          behandlung_reinigung: latest.behandlung_reinigung || '',
          behandlung_spuelloesung: latest.behandlung_spuelloesung || '',
          behandlung_primaerverband: latest.behandlung_primaerverband || '',
          behandlung_sekundaerverband: latest.behandlung_sekundaerverband || '',
          behandlung_fixierung: latest.behandlung_fixierung || '',
          behandlung_kompression: latest.behandlung_kompression || '',
          behandlung_lagerung: latest.behandlung_lagerung || '',
          ursachen: latest.ursachen || [], pflegeziele: latest.pflegeziele || [],
          vw_frequenz: latest.vw_frequenz || '',
          pflegefachkraft: user?.name || ''
        }));
      }
    } catch (err) { console.error(err); }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const updateWundgrund = (key, val) => setForm(prev => ({ ...prev, wundgrund: { ...prev.wundgrund, [key]: val } }));
  const toggleList = (key, item) => setForm(prev => {
    const list = prev[key] || [];
    return { ...prev, [key]: list.includes(item) ? list.filter(i => i !== item) : [...list, item] };
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const res = await uploadAPI.photo(file); update('foto', res.data.url); toast.success('Foto hochgeladen'); }
    catch { toast.error('Foto-Upload fehlgeschlagen'); }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try { await reportAPI.create(form); toast.success('Wundbericht gespeichert!'); navigate(`/wunde/${woundId}`); }
    catch { toast.error('Fehler beim Speichern'); }
    finally { setLoading(false); }
  };

  const flaeche = Math.round(form.laenge * form.breite * 100) / 100;

  const ToggleButtons = ({ options, selected, onToggle, colorClass = 'bg-teal-50 border-teal-300 text-teal-700' }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => onToggle(opt)}
          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all min-h-[44px] ${
            selected.includes(opt) ? colorClass : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          }`} data-testid={`toggle-${opt.replace(/\s/g, '-').toLowerCase()}`}>
          {opt}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="new-wound-report-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-heading font-bold text-slate-900">
            {wound?.reports?.length > 0 ? 'Folgebericht' : 'Neuer Wundbericht'}
          </h1>
          {wound && <p className="text-xs text-slate-500">{wound.patient?.name} &middot; {wound.wundart}</p>}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-0.5 overflow-x-auto" data-testid="step-indicator">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setStep(i)}
              className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all min-w-[44px] ${
                i === step ? 'bg-teal-50 text-teal-700' : i < step ? 'text-teal-600' : 'text-slate-300'
              }`} data-testid={`step-${s.id}`}>
              <Icon className="w-4 h-4" />
              <span className="text-[9px] font-medium hidden sm:block">{s.label}</span>
            </button>
          );
        })}
      </div>

      <div className="animate-fade-in">
        {/* ─── Step 0: Foto ─── */}
        {step === 0 && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Camera className="w-5 h-5 text-teal-700" />Wundfoto</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {form.foto ? (
                <div className="relative">
                  <img src={form.foto} alt="Wundfoto" className="w-full rounded-xl max-h-64 object-cover" data-testid="wound-photo-preview" />
                  <Button variant="outline" size="sm" className="absolute bottom-2 right-2" onClick={() => fileInputRef.current?.click()} data-testid="change-photo-btn">Foto ändern</Button>
                </div>
              ) : (
                <button onClick={() => fileInputRef.current?.click()} className="w-full h-48 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-teal-300 hover:bg-teal-50/50 transition-all" data-testid="upload-photo-btn">
                  <Upload className="w-8 h-8 text-slate-300" /><span className="text-sm text-slate-500">Foto aufnehmen oder hochladen</span>
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-file-input" />
            </CardContent>
          </Card>
        )}

        {/* ─── Step 1: Messung ─── */}
        {step === 1 && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Ruler className="w-5 h-5 text-teal-700" />Wundvermessung</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[['Länge', 'laenge'], ['Breite', 'breite'], ['Tiefe', 'tiefe']].map(([lbl, key]) => (
                  <div key={key} className="space-y-2">
                    <Label>{lbl} (cm)</Label>
                    <Input data-testid={`${key}-input`} type="number" step="0.1" min="0" value={form[key] || ''} onChange={e => update(key, parseFloat(e.target.value) || 0)} className="h-12 text-center text-lg" placeholder="0.0" />
                  </div>
                ))}
              </div>
              <div className="bg-teal-50 rounded-xl p-4 text-center">
                <p className="text-xs text-teal-600 uppercase tracking-wider font-medium">Berechnete Fläche</p>
                <p className="text-3xl font-bold text-teal-800 mt-1" data-testid="calculated-area">{flaeche} cm²</p>
              </div>
              <div className="space-y-3">
                <Label className="text-sm font-medium">Wundgrund (%)</Label>
                {['granulation', 'fibrin', 'nekrose', 'epithelisierung'].map(key => (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 capitalize">{key}</span>
                      <span className="text-xs font-medium text-slate-700">{form.wundgrund[key]}%</span>
                    </div>
                    <Slider data-testid={`wundgrund-${key}`} value={[form.wundgrund[key]]} onValueChange={([v]) => updateWundgrund(key, v)} max={100} step={5} className="cursor-pointer" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Step 2: Assessment ─── */}
        {step === 2 && (
          <div className="space-y-4">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Wundrand & Umgebung</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label className="text-sm mb-2 block">Wundrand</Label>
                  <ToggleButtons options={WUNDRAND_OPTIONS} selected={form.wundrand} onToggle={(o) => toggleList('wundrand', o)} /></div>
                <div><Label className="text-sm mb-2 block">Wundumgebung</Label>
                  <ToggleButtons options={WUNDUMGEBUNG_OPTIONS} selected={form.wundumgebung} onToggle={(o) => toggleList('wundumgebung', o)} /></div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Exsudat & Wundphase</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[['Menge', 'exsudat_menge', ['kein', 'gering', 'mittel', 'stark']],
                    ['Art', 'exsudat_art', ['klar', 'serös', 'blutig', 'eitrig']],
                    ['Geruch', 'exsudat_geruch', ['nein', 'ja']]].map(([lbl, key, opts]) => (
                    <div key={key} className="space-y-2"><Label>{lbl}</Label>
                      <Select value={form[key]} onValueChange={v => update(key, v)}>
                        <SelectTrigger className="h-12" data-testid={`${key}-select`}><SelectValue placeholder={lbl} /></SelectTrigger>
                        <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select></div>
                  ))}
                </div>
                <div className="space-y-2"><Label>Wundphase</Label>
                  <Select value={form.wundphase} onValueChange={v => update('wundphase', v)}>
                    <SelectTrigger className="h-12" data-testid="wundphase-select"><SelectValue placeholder="Wundphase" /></SelectTrigger>
                    <SelectContent>{['Reinigungsphase', 'Granulationsphase', 'Epithelisierungsphase', 'Exsudationsphase'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base text-rose-700">Infektionszeichen</CardTitle></CardHeader>
              <CardContent>
                <ToggleButtons options={INFEKTIONSZEICHEN_OPTIONS} selected={form.infektionszeichen} onToggle={(o) => toggleList('infektionszeichen', o)} colorClass="bg-rose-50 border-rose-300 text-rose-700" />
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Schmerz</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2"><Label>NRS (0-10)</Label>
                    <span className={`text-lg font-bold ${form.schmerz_nrs >= 7 ? 'text-rose-600' : form.schmerz_nrs >= 4 ? 'text-amber-600' : 'text-emerald-600'}`}>{form.schmerz_nrs}</span></div>
                  <Slider data-testid="schmerz-nrs-slider" value={[form.schmerz_nrs]} onValueChange={([v]) => update('schmerz_nrs', v)} max={10} step={1} className="cursor-pointer" />
                  <div className="flex justify-between text-xs text-slate-400 mt-1"><span>Kein Schmerz</span><span>Stärkster Schmerz</span></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Schmerzqualität</Label>
                    <Select value={form.schmerz_qualitaet} onValueChange={v => update('schmerz_qualitaet', v)}>
                      <SelectTrigger className="h-12" data-testid="schmerz-qualitaet-select"><SelectValue placeholder="Qualität" /></SelectTrigger>
                      <SelectContent>{['stechend', 'brennend', 'pochend', 'dumpf'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Schmerzzeitpunkt</Label>
                    <Select value={form.schmerz_zeitpunkt} onValueChange={v => update('schmerz_zeitpunkt', v)}>
                      <SelectTrigger className="h-12" data-testid="schmerz-zeitpunkt-select"><SelectValue placeholder="Zeitpunkt" /></SelectTrigger>
                      <SelectContent>{['Ruhe', 'Belastung', 'Verbandwechsel'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Step 3: Ursachen & Risikoanalyse + Pflegeziele ─── */}
        {step === 3 && (
          <div className="space-y-4">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Target className="w-5 h-5 text-teal-700" />Ursachen & Risikoanalyse</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label className="text-sm mb-2 block">Ursachen</Label>
                  <ToggleButtons options={URSACHEN_OPTIONS} selected={form.ursachen} onToggle={(o) => toggleList('ursachen', o)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Braden-Skala (Punkte)</Label>
                    <Input data-testid="braden-skala-input" type="number" min="6" max="23" value={form.braden_skala || ''} onChange={e => update('braden_skala', parseInt(e.target.value) || 0)} className="h-12" placeholder="6-23" /></div>
                  <div className="space-y-2"><Label>Braden Datum</Label>
                    <Input data-testid="braden-datum-input" type="date" value={form.braden_datum} onChange={e => update('braden_datum', e.target.value)} className="h-12" /></div>
                </div>
                <div className="space-y-2"><Label>Weitere Risiken</Label>
                  <Textarea data-testid="weitere-risiken-input" value={form.weitere_risiken} onChange={e => update('weitere_risiken', e.target.value)} placeholder="Weitere Risikofaktoren..." rows={2} /></div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-teal-700" />Pflegeziele</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <ToggleButtons options={PFLEGEZIELE_OPTIONS} selected={form.pflegeziele} onToggle={(o) => toggleList('pflegeziele', o)} />
                <div className="space-y-2"><Label>Zieltermin</Label>
                  <Input data-testid="zieltermin-input" type="date" value={form.zieltermin} onChange={e => update('zieltermin', e.target.value)} className="h-12" /></div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Step 4: Behandlung ─── */}
        {step === 4 && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Pill className="w-5 h-5 text-teal-700" />Behandlung / Maßnahmen</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Wundreinigung</Label>
                  <Select value={form.behandlung_reinigung} onValueChange={v => update('behandlung_reinigung', v)}>
                    <SelectTrigger className="h-12" data-testid="reinigung-select"><SelectValue placeholder="Methode" /></SelectTrigger>
                    <SelectContent>{['Spülung mit NaCl 0.9%', 'Spülung mit Ringer-Lösung', 'Chirurgisches Débridement', 'Enzymatisches Débridement', 'Autolytisches Débridement', 'Inspektion'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Spüllösung</Label>
                  <Select value={form.behandlung_spuelloesung} onValueChange={v => update('behandlung_spuelloesung', v)}>
                    <SelectTrigger className="h-12" data-testid="spuelloesung-select"><SelectValue placeholder="Lösung" /></SelectTrigger>
                    <SelectContent>{['NaCl 0.9%', 'Ringer-Lösung', 'Octenisept', 'Polihexanid', 'Leitungswasser'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Primärverband</Label>
                  <Select value={form.behandlung_primaerverband} onValueChange={v => update('behandlung_primaerverband', v)}>
                    <SelectTrigger className="h-12" data-testid="primaerverband-select"><SelectValue placeholder="Verband" /></SelectTrigger>
                    <SelectContent>{['Hydrogel', 'Alginate', 'Schaumverband', 'Hydrokolloide', 'Silber-Wundauflage', 'Aktivkohle-Verband', 'Kollagen-Wundauflage', 'Hautschutzfilm'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Sekundärverband</Label>
                  <Select value={form.behandlung_sekundaerverband} onValueChange={v => update('behandlung_sekundaerverband', v)}>
                    <SelectTrigger className="h-12" data-testid="sekundaerverband-select"><SelectValue placeholder="Verband" /></SelectTrigger>
                    <SelectContent>{['Schaumverband', 'Superabsorber', 'Mullkompresse', 'Vliesstoffkompresse'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Fixierung</Label>
                  <Select value={form.behandlung_fixierung} onValueChange={v => update('behandlung_fixierung', v)}>
                    <SelectTrigger className="h-12" data-testid="fixierung-select"><SelectValue placeholder="Fixierung" /></SelectTrigger>
                    <SelectContent>{['Folienverband', 'Mullbinde', 'Kurzzugbinde', 'Netzverband', 'Klebevlies'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Kompression</Label>
                  <Input data-testid="kompression-input" value={form.behandlung_kompression} onChange={e => update('behandlung_kompression', e.target.value)} placeholder="z.B. Klasse II" className="h-12" /></div>
              </div>
              <div className="space-y-2"><Label>Lagerung / Druckentlastung</Label>
                <Input data-testid="lagerung-input" value={form.behandlung_lagerung} onChange={e => update('behandlung_lagerung', e.target.value)} placeholder="z.B. 30°-Seitenlagerung" className="h-12" /></div>
              <div className="space-y-2"><Label>Schmerzmanagement</Label>
                <Input data-testid="schmerzmanagement-input" value={form.schmerzmanagement} onChange={e => update('schmerzmanagement', e.target.value)} placeholder="Maßnahmen bei NRS > ..." className="h-12" /></div>
              <div className="space-y-2"><Label>Medikamente (lokal/systemisch)</Label>
                <Input data-testid="medikamente-input" value={form.behandlung_medikamente} onChange={e => update('behandlung_medikamente', e.target.value)} placeholder="z.B. Antibiotikum" className="h-12" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>VW-Frequenz</Label>
                  <Select value={form.vw_frequenz} onValueChange={v => update('vw_frequenz', v)}>
                    <SelectTrigger className="h-12" data-testid="vw-frequenz-select"><SelectValue placeholder="Häufigkeit" /></SelectTrigger>
                    <SelectContent>{['täglich', 'alle 2 Tage', '3x / Woche', '2x / Woche', '1x / Woche', 'bei Bedarf'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Nächster VW</Label>
                  <Input data-testid="naechster-vw-input" type="date" value={form.naechster_vw} onChange={e => update('naechster_vw', e.target.value)} className="h-12" /></div>
              </div>
              <div className="space-y-2"><Label>Begründung der Maßnahmen</Label>
                <Textarea data-testid="begruendung-input" value={form.behandlung_begruendung} onChange={e => update('behandlung_begruendung', e.target.value)} placeholder="Begründung für die gewählten Maßnahmen..." rows={2} /></div>
            </CardContent>
          </Card>
        )}

        {/* ─── Step 5: Ärztliche Einbindung + Evaluation ─── */}
        {step === 5 && (
          <div className="space-y-4">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><UserCheck className="w-5 h-5 text-teal-700" />Ärztliche Einbindung</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Arzt informiert</Label>
                    <Select value={form.arzt_informiert} onValueChange={v => update('arzt_informiert', v)}>
                      <SelectTrigger className="h-12" data-testid="arzt-informiert-select"><SelectValue placeholder="Auswählen" /></SelectTrigger>
                      <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nein">Nein</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Verordnung vorhanden</Label>
                    <Select value={form.verordnung_vorhanden} onValueChange={v => update('verordnung_vorhanden', v)}>
                      <SelectTrigger className="h-12" data-testid="verordnung-select"><SelectValue placeholder="Auswählen" /></SelectTrigger>
                      <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nein">Nein</SelectItem></SelectContent>
                    </Select></div>
                </div>
                {form.arzt_informiert === 'ja' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Datum</Label>
                      <Input data-testid="arzt-datum-input" type="date" value={form.arzt_datum} onChange={e => update('arzt_datum', e.target.value)} className="h-12" /></div>
                    <div className="space-y-2"><Label>Anlass</Label>
                      <Input data-testid="arzt-anlass-input" value={form.arzt_anlass} onChange={e => update('arzt_anlass', e.target.value)} placeholder="z.B. Wundverschlechterung" className="h-12" /></div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-teal-700" />Evaluation / Verlaufsbewertung</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Wundzustand</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[['verbessert', 'bg-emerald-50 border-emerald-300 text-emerald-700'],
                      ['unverändert', 'bg-amber-50 border-amber-300 text-amber-700'],
                      ['verschlechtert', 'bg-rose-50 border-rose-300 text-rose-700']].map(([val, cls]) => (
                      <button key={val} type="button" onClick={() => update('evaluation_status', val)}
                        className={`p-3 rounded-xl text-sm font-medium border transition-all min-h-[44px] ${
                          form.evaluation_status === val ? cls : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`} data-testid={`eval-${val}`}>{val}</button>
                    ))}
                  </div></div>
                <div className="space-y-2"><Label>Begründung</Label>
                  <Textarea data-testid="eval-begruendung-input" value={form.evaluation_begruendung} onChange={e => update('evaluation_begruendung', e.target.value)} placeholder="Begründung der Bewertung..." rows={2} /></div>
                <div className="space-y-2"><Label>Maßnahmen angepasst</Label>
                  <Select value={form.massnahmen_angepasst} onValueChange={v => update('massnahmen_angepasst', v)}>
                    <SelectTrigger className="h-12" data-testid="massnahmen-angepasst-select"><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent><SelectItem value="ja">Ja</SelectItem><SelectItem value="nein">Nein</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Bemerkungen</Label>
                  <Textarea data-testid="bemerkungen-input" value={form.bemerkungen} onChange={e => update('bemerkungen', e.target.value)} placeholder="Zusätzliche Beobachtungen..." rows={2} /></div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Step 6: Zusammenfassung ─── */}
        {step === 6 && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Check className="w-5 h-5 text-teal-700" />Zusammenfassung</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Fläche</p><p className="text-lg font-bold text-slate-900">{flaeche} cm²</p><p className="text-xs text-slate-500">{form.laenge} x {form.breite} x {form.tiefe} cm</p></div>
                <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Schmerz</p><p className={`text-lg font-bold ${form.schmerz_nrs >= 7 ? 'text-rose-600' : form.schmerz_nrs >= 4 ? 'text-amber-600' : 'text-emerald-600'}`}>NRS {form.schmerz_nrs}/10</p></div>
              </div>
              {form.wundphase && <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Wundphase</p><p className="text-sm font-medium text-slate-700">{form.wundphase}</p></div>}
              {form.infektionszeichen.length > 0 && <div className="bg-rose-50 rounded-xl p-3 border border-rose-200"><p className="text-xs text-rose-600 uppercase tracking-wider font-bold">Infektionszeichen</p><p className="text-sm text-rose-700 mt-1">{form.infektionszeichen.join(', ')}</p></div>}
              {form.ursachen.length > 0 && <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Ursachen</p><p className="text-sm text-slate-700">{form.ursachen.join(', ')}</p></div>}
              {form.braden_skala > 0 && <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Braden-Skala</p><p className="text-sm text-slate-700">{form.braden_skala} Punkte</p></div>}
              {form.pflegeziele.length > 0 && <div className="bg-teal-50 rounded-xl p-3 border border-teal-200"><p className="text-xs text-teal-600 uppercase tracking-wider">Pflegeziele</p><p className="text-sm text-teal-700">{form.pflegeziele.join(', ')}</p></div>}
              {form.behandlung_primaerverband && <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Behandlung</p><p className="text-sm text-slate-700">{form.behandlung_reinigung}</p><p className="text-sm text-slate-700">{form.behandlung_primaerverband} / {form.behandlung_sekundaerverband}</p></div>}
              {form.arzt_informiert && <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400 uppercase tracking-wider">Arzt informiert</p><p className="text-sm text-slate-700">{form.arzt_informiert}{form.arzt_anlass ? ` - ${form.arzt_anlass}` : ''}</p></div>}
              {form.evaluation_status && <div className={`rounded-xl p-3 border ${form.evaluation_status === 'verbessert' ? 'bg-emerald-50 border-emerald-200' : form.evaluation_status === 'verschlechtert' ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`}><p className="text-xs uppercase tracking-wider font-bold">Evaluation: {form.evaluation_status}</p>{form.evaluation_begruendung && <p className="text-sm mt-1">{form.evaluation_begruendung}</p>}</div>}
              {form.naechster_vw && <div className="bg-teal-50 rounded-xl p-3 border border-teal-200"><p className="text-xs text-teal-600 uppercase tracking-wider">Nächster VW</p><p className="text-sm font-medium text-teal-800">{new Date(form.naechster_vw).toLocaleDateString('de-DE')}</p>{form.vw_frequenz && <p className="text-xs text-teal-600">{form.vw_frequenz}</p>}</div>}
              <div className="space-y-2"><Label>Pflegefachkraft</Label><Input data-testid="pflegefachkraft-input" value={form.pflegefachkraft} onChange={e => update('pflegefachkraft', e.target.value)} className="h-12" /></div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pb-4">
        {step > 0 && (
          <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="flex-1 h-12 rounded-xl" data-testid="prev-step-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Zurück
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button type="button" onClick={() => setStep(step + 1)} className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="next-step-btn">
            Weiter<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button type="button" onClick={handleSubmit} disabled={loading} className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="submit-report-btn">
            <Check className="w-4 h-4 mr-2" />{loading ? 'Wird gespeichert...' : 'Bericht speichern'}
          </Button>
        )}
      </div>
    </div>
  );
}
